import * as React from "react";
import { TextField } from "@fluentui/react/lib/TextField";
import { InputProps } from "../../interfaces/props/InputProps";
import { RootState } from "../../redux";
import { connect, ConnectedProps } from "react-redux";
import { sharePointInternalFileName, sharePointInternalTitleName } from "../../config/constants";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { setFile, updateFileName } from "../../redux/modules/file";
import { setIsCheckingForDuplicates } from "../../redux/modules/savingInBackground";
import generalModule from "../../../../utilities/GeneralModule";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { ContentManagerCopyMoveModeEnum } from "../enums/ContentManagerCopyMoveModeEnum";
import { DragAndDropHelper } from "../../../../utilities/DragAndDropHelper";
import { formatNameFieldValue } from "../../services/FileFolderNameExtensions";

const mapStateToProps = (state: RootState) => {
  return {
    selectedFiles: !DragAndDropHelper.isDroppedModeActive ? state.files?.files?.filter(item => item.isSelected) : state.files?.files?.filter(item => item.isSelected && item.isFileSystemEntry),
    moveTo: state.contentManagerDirectory?.moveTo
  };
};

const mapDispatchToProps = {
  updateFileName,
  setIsCheckingForDuplicates,
  setFile
};

const connector = connect(mapStateToProps, mapDispatchToProps);
interface TextInputProps extends InputProps, ConnectedProps<typeof connector> { }

const NormalTextInput: React.FunctionComponent<TextInputProps> = ({
  displayName,
  internalName,
  isRequired,
  inputValue,
  updateInputValidationStatus,
  inputValidator,
  error,
  selectedFiles,
  moveTo
}: TextInputProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const [isNameInput] = React.useState(internalName?.trim().toLowerCase() === sharePointInternalFileName.toLowerCase());
  const [isTitleInput] = React.useState(internalName?.trim().toLowerCase() === sharePointInternalTitleName.toLowerCase());
  const [selectedIndexes, setSelectedIndexes] = React.useState('');

  React.useEffect(() => {
    const indexes = selectedFiles?.map(x => x.index).join(',');
    if (selectedIndexes !== indexes) {
      setSelectedIndexes(indexes);
    }
  }, [selectedFiles]);

  React.useEffect(() => {
    updateTextInput();
  }, [selectedIndexes]);

  const isFirstRun = React.useRef(true);
  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      updateInputValidationStatus(inputValidator.validate(inputValue?.value));
    } catch (e) {
      logger(e, "normalTextInput.tsx (useEffect 2)")
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  const updateTextInput = () => {
    if ((selectedFiles?.length > 1 || moveTo?.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails) && isTitleInput && !generalModule.getIsFileInfoPanelOpen() && !generalModule.getIsMultiMetaFileUpdatePanelOpen()) {
      updateInputValidationStatus(inputValidator.validate(languageStrings.TitleAutoSet));
    }
    else if (selectedFiles?.length === 1 && isTitleInput && !generalModule.getIsFileInfoPanelOpen()) {
      updateInputValidationStatus(inputValidator.validate(selectedFiles[0].name));
      return;
    }
  };

  const handleTextFieldOnchange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue: string
  ): void => {
    updateInputValidationStatus(inputValidator.validate(!isNameInput ? newValue : formatNameFieldValue(newValue)));
  };

  return (
    <TextField
      disabled={((selectedFiles?.length > 1 || moveTo?.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails) && isTitleInput && !generalModule.getIsFileInfoPanelOpen() && !generalModule.getIsMultiMetaFileUpdatePanelOpen()) || inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      label={displayName}
      type="text"
      value={inputValue?.value ?? ""}
      onChange={handleTextFieldOnchange}
      required={isRequired()}
      errorMessage={error}
      className="xb-sp-input"
      suffix={inputValue?.extention ? `.${inputValue?.extention}` : undefined}
    />
  );
};

export default connector(NormalTextInput);